import { cn } from '@/lib/utils'

export const NOTES = [50000, 20000, 10000, 5000, 2000, 1000, 500], COINS = [200, 100, 50, 20, 10, 5, 2, 1]
export const denominationName = (cents: number) => cents >= 100 ? `${String(cents / 100)} €` : `${String(cents)} ct`

// Own drawings in the colours of the real money. ECB images would need a SPECIMEN overlay across the note.
const NOTE_COLORS: Record<number, { light: string; paper: string; ink: string }> = {
  500: { light: '#f4f5f6', paper: '#dfe2e5', ink: '#4f565f' }, 1000: { light: '#fdeeec', paper: '#f3cdc9', ink: '#a12f29' },
  2000: { light: '#eef4fd', paper: '#cfdef5', ink: '#23519b' }, 5000: { light: '#fef1e6', paper: '#f8d5bb', ink: '#a9530f' },
  10000: { light: '#ecf7ef', paper: '#cce8d4', ink: '#22683a' }, 20000: { light: '#fcf7dd', paper: '#f1e5a8', ink: '#7a640a' },
  50000: { light: '#f5eefa', paper: '#e1d1eb', ink: '#5e3478' },
}
const GOLD = ['#f6e8ad', '#dfc362', '#b08c2a'], SILVER = ['#f5f6f8', '#d2d6db', '#9aa1a9'], COPPER = ['#efc3a1', '#cb8d5e', '#8f522a']
const RINGS = [8, 13, 18, 23, 28, 33, 38, 43]

function Note({ cents, className }: { cents: number; className?: string }) {
  const { light, paper, ink } = NOTE_COLORS[cents]
  return <svg aria-hidden viewBox="0 0 120 64" className={cn('h-auto w-full shrink-0', className)}>
    <defs>
      <linearGradient id={`note-${String(cents)}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={light} /><stop offset="1" stopColor={paper} /></linearGradient>
      <clipPath id={`note-edge-${String(cents)}`}><rect width="120" height="64" rx="5" /></clipPath>
    </defs>
    <rect x="0.5" y="0.5" width="119" height="63" rx="4.5" fill={`url(#note-${String(cents)})`} stroke={ink} strokeOpacity="0.4" />
    <g clipPath={`url(#note-edge-${String(cents)})`} fill="none" stroke={ink} strokeOpacity="0.2" strokeWidth="0.7">
      {RINGS.map(r => <circle key={r} cx="22" cy="32" r={r} />)}
    </g>
    <text x="22" y="38" textAnchor="middle" fontSize="17" fill={ink} className="font-menu">€</text>
    <text x="111" y="38" textAnchor="end" fontSize="31" fill={ink} className="font-menu">{cents / 100}</text>
    <text x="111" y="53" textAnchor="end" fontSize="8" fontWeight="600" letterSpacing="1.4" fill={ink} fillOpacity="0.8">EURO</text>
  </svg>
}
function Metal({ id, tones }: { id: string; tones: string[] }) {
  return <radialGradient id={id} cx="0.5" cy="0.5" r="0.75" fx="0.32" fy="0.26">
    <stop offset="0" stopColor={tones[0]} /><stop offset="0.55" stopColor={tones[1]} /><stop offset="1" stopColor={tones[2]} />
  </radialGradient>
}
function Coin({ cents, className }: { cents: number; className?: string }) {
  const euro = cents >= 100, label = euro ? cents / 100 : cents
  const [ring, center] = cents === 200 ? [SILVER, GOLD] : cents === 100 ? [GOLD, SILVER] : cents >= 10 ? [GOLD, GOLD] : [COPPER, COPPER]
  const ink = ring === COPPER ? '#47210b' : '#382a0e', twoTone = ring !== center, id = `coin-${String(cents)}`
  return <svg aria-hidden viewBox="0 0 64 64" className={cn('h-auto w-full shrink-0', className)}>
    <defs><Metal id={`${id}-ring`} tones={ring} /><Metal id={`${id}-center`} tones={center} /></defs>
    <circle cx="32" cy="32" r="31" fill={`url(#${id}-ring)`} stroke={ring[2]} />
    <circle cx="32" cy="32" r={twoTone ? 22 : 26.5} fill={`url(#${id}-center)`} stroke={center[2]} strokeOpacity={twoTone ? 0.9 : 0.5} strokeWidth="0.75" />
    <text x="32" y={euro ? 37 : 36} textAnchor="middle" fontSize={euro ? 24 : 21} fill={ink} className="font-menu">{label}</text>
    <text x="32" y="46.5" textAnchor="middle" fontSize="6.5" fontWeight="600" letterSpacing="0.9" fill={ink} fillOpacity="0.85">{euro ? 'EURO' : 'CENT'}</text>
  </svg>
}
/** Money is recognised by its look, so the buttons and counting rows show it rather than a bare number. */
export function CashMark({ cents, className }: { cents: number; className?: string }) {
  return cents >= 500 ? <Note cents={cents} className={className} /> : <Coin cents={cents} className={className} />
}
